import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Check, X } from "lucide-react";
import { http, formatApiError } from "../../lib/api";

export const CAT_COLORS = ["#DC2626", "#EA580C", "#F59E0B", "#16A34A", "#0284C7", "#4F46E5", "#7C3AED", "#64748B"];

export const CodeBadge = ({ code, color, testid }) => code ? (
  <span data-testid={testid} className="inline-flex items-center font-mono text-[11px] font-extrabold rounded px-1.5 py-0.5 text-white" style={{ background: color || "#64748B" }}>{code}</span>
) : null;

const CatForm = ({ init, onSave, onCancel }) => {
  const [name, setName] = useState(init?.name || "");
  const [color, setColor] = useState(init?.color || CAT_COLORS[4]);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cat-form">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom de la catégorie (ex. Tests et vérifications)" className="border border-slate-300 rounded-md px-2 py-1.5 text-sm flex-1 min-w-[220px]" data-testid="cat-name" />
      <div className="flex gap-1" data-testid="cat-colors">
        {CAT_COLORS.map((c) => <button key={c} type="button" onClick={() => setColor(c)} className={`h-6 w-6 rounded-full ring-offset-1 ${color === c ? "ring-2 ring-slate-900" : ""}`} style={{ background: c }} data-testid={`cat-color-${c.slice(1)}`} aria-label={c} />)}
        <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} className="h-6 w-8 cursor-pointer" data-testid="cat-color-custom" title="Autre couleur" />
      </div>
      <button type="button" disabled={!name.trim()} onClick={() => onSave({ name, color })} className="p-1.5 rounded bg-emerald-600 text-white disabled:opacity-40" data-testid="cat-save"><Check className="h-4 w-4" /></button>
      <button type="button" onClick={onCancel} className="p-1.5 rounded hover:bg-slate-100" data-testid="cat-cancel"><X className="h-4 w-4" /></button>
    </div>
  );
};

export const CategoriesPanel = ({ cats, onChanged, canEdit }) => {
  const [edit, setEdit] = useState(null);
  const save = async (v) => {
    try {
      if (edit.id) await http.put(`/report-categories/${edit.id}`, v); else await http.post("/report-categories", v);
      setEdit(null); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (c) => {
    if (!window.confirm(`Archiver la catégorie ${c.number} « ${c.name} » ?`)) return;
    try { await http.post(`/report-categories/${c.id}/archive`); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-2" data-testid="cat-panel">
      <div className="flex items-center gap-2">
        <h2 className="font-display font-bold text-lg">Catégories de rapports</h2>
        <span className="text-xs text-slate-500">numérotées 01 à 99 · le code d'un rapport = n° de catégorie + n° du rapport (ex. 0101)</span>
        {canEdit && !edit && <button type="button" onClick={() => setEdit({})} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[#0B2A7A]" data-testid="cat-new"><Plus className="h-4 w-4" />Catégorie</button>}
      </div>
      {edit && !edit.id && <CatForm onSave={save} onCancel={() => setEdit(null)} />}
      {cats.length === 0 && !edit && <p className="text-sm text-slate-500">Aucune catégorie. Exemple : 01 Tests et vérifications, 02 Incidents, 03 Rondes.</p>}
      <ul className="divide-y divide-slate-100">
        {cats.map((c) => (
          <li key={c.id} className="py-2 flex items-center gap-3" data-testid={`cat-row-${c.id}`}>
            {edit?.id === c.id ? <CatForm init={c} onSave={save} onCancel={() => setEdit(null)} /> : <>
              <CodeBadge code={c.number} color={c.color} testid={`cat-number-${c.id}`} />
              <span className="font-semibold text-sm">{c.name}</span>
              {canEdit && <span className="ml-auto flex gap-1">
                <button type="button" onClick={() => setEdit(c)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`cat-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={() => archive(c)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`cat-archive-${c.id}`}><Archive className="h-4 w-4" /></button>
              </span>}
            </>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const CategorySelect = ({ tpl, cats, onChanged, canEdit }) => {
  const set = async (v) => {
    try { const r = await http.put(`/report-templates/${tpl.id}/category`, { category_id: v || null }); toast.success(r.data.code ? `Code attribué : ${r.data.code}` : "Catégorie retirée"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <select disabled={!canEdit} value={tpl.category_id || ""} onChange={(e) => set(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm max-w-[200px]" data-testid={`tpl-category-${tpl.id}`}>
      <option value="">Sans catégorie</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.number} · {c.name}</option>)}
    </select>
  );
};
